'use client'

import { useState } from 'react'
import { CheckIcon, MinusIcon, PlusIcon, SparklesIcon as SparklesSolidIcon } from '@heroicons/react/24/solid'
import { CheckIcon as CheckOutlineIcon, InformationCircleIcon, CalculatorIcon } from '@heroicons/react/24/outline'
import { getWhatsAppUrl } from '../../../../config/contact'

const SERVICE_PRICE = 999;
const STAND_PRICE = 250;
const STICKER_PRICE = 25;
const MAX_ITEMS = 5;

const features = [
  'Unlimited Menu Updates',
  'AI Menu Digitisation',
  'Searchable & Filterable Menus',
  'Scan Analytics Dashboard',
  'Priority Support',
  'Up to 5 QR Stands & Stickers',
];

export default function PricingSection() {
  const [numStands, setNumStands] = useState(5);
  const [numStickers, setNumStickers] = useState(0);

  const standsCost = numStands * STAND_PRICE;
  const stickersCost = numStickers * STICKER_PRICE;
  const total = SERVICE_PRICE + standsCost + stickersCost;
  const perDay = Math.ceil(total / 365);
  const totalItems = numStands + numStickers;

  const getOrderWhatsAppUrl = () => {
    const message = encodeURIComponent(
      `Hi ScanMaadi! I'd like to order:\n` +
      `- 1x Digital Menu Service (₹${SERVICE_PRICE}/year)\n` +
      (numStands > 0 ? `- ${numStands}x QR Stand (₹${standsCost})\n` : '') +
      (numStickers > 0 ? `- ${numStickers}x QR Sticker (₹${stickersCost})\n` : '') +
      `Total: ₹${total}/year`
    );
    return getWhatsAppUrl(message);
  };

  return (
    <div id="pricing" className="relative isolate bg-white px-6 py-24 sm:py-32 lg:px-8 overflow-hidden">
      {/* Decorative background blur */}
      <div aria-hidden="true" className="absolute inset-x-0 -top-20 -z-10 transform-gpu overflow-hidden blur-3xl">
        <div
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
          className="mx-auto aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-primary-300/30 to-accent-300/30 opacity-30"
        />
      </div>
      <div aria-hidden="true" className="absolute inset-x-0 -bottom-20 -z-10 transform-gpu overflow-hidden blur-3xl">
        <div
          style={{
            clipPath:
              'polygon(20% 0%, 0% 20%, 30% 50%, 0% 80%, 20% 100%, 50% 80%, 80% 100%, 100% 80%, 70% 50%, 100% 20%, 80% 0%, 50% 20%)',
          }}
          className="mx-auto aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tl from-highlight-300/30 to-primary-300/30 opacity-40"
        />
      </div>

      {/* Section Header */}
      <div className="mx-auto max-w-4xl text-center">
        <SparklesSolidIcon className="h-10 w-10 text-primary-500 mx-auto mb-3" />
        <h2 className="text-base font-semibold leading-7 text-primary-600">Pricing</h2>
        <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          One Plan.{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
            Simple Pricing.
          </span>
        </p>
      </div>
      <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
        Everything your restaurant needs to go digital. No tiers, no confusion.
      </p>

      {/* Hero Price Card */}
      <div className="mx-auto mt-12 max-w-xl">
        <div className="relative rounded-3xl bg-gradient-to-br from-primary-600 to-primary-500 text-white p-8 sm:p-10 shadow-2xl">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform px-4 py-1 bg-highlight-400 text-text-700 text-xs font-semibold rounded-full shadow-lg uppercase tracking-wider">
            All-Inclusive Digital Menu
          </div>

          <div className="mt-4 flex items-baseline gap-x-2">
            <span className="text-5xl sm:text-6xl font-bold tracking-tight">₹{SERVICE_PRICE}</span>
            <span className="text-xl text-gray-200 font-medium">/year</span>
          </div>

          <p className="mt-4 text-sm text-primary-100 leading-relaxed">
            You spend ₹5,000+ reprinting paper menus every year. Save with ScanMaadi.
          </p>

          <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-200">
            {features.map((feature) => (
              <li key={feature} className="flex gap-x-3 items-start">
                <CheckOutlineIcon className="h-6 w-5 flex-none text-highlight-400" aria-hidden="true" />
                {feature}
              </li>
            ))}
          </ul>

          <a
            href={getWhatsAppUrl(encodeURIComponent("Hi ScanMaadi! I'm interested in your ₹999/year Digital Menu Service."))}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 block rounded-md bg-white px-3.5 py-3 text-center text-sm font-semibold text-primary-600 shadow-sm hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all duration-200 hover:scale-[1.03]"
          >
            Get Started on WhatsApp
          </a>
        </div>
      </div>

      {/* Physical Products */}
      <div className="mx-auto mt-16 max-w-2xl">
        <h3 className="text-center text-xl font-semibold text-gray-900 mb-6">Choose Your Table QR</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* QR Stand Card */}
          <div className="relative rounded-2xl bg-white ring-1 ring-gray-900/10 p-6 text-center shadow-sm">
            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 transform px-3 py-0.5 bg-highlight-400 text-text-700 text-xs font-semibold rounded-full shadow uppercase tracking-wider">
              Popular
            </div>
            <div className="mt-2 text-3xl font-bold text-gray-900">₹{STAND_PRICE}</div>
            <div className="text-sm text-gray-500 font-medium">/stand</div>
            <h4 className="mt-3 text-base font-semibold text-primary-600">QR Stand</h4>
            <p className="mt-1 text-sm text-gray-600">Premium acrylic stand for your tables</p>
          </div>

          {/* QR Sticker Card */}
          <div className="rounded-2xl bg-white ring-1 ring-gray-900/10 p-6 text-center shadow-sm">
            <div className="mt-2 text-3xl font-bold text-gray-900">₹{STICKER_PRICE}</div>
            <div className="text-sm text-gray-500 font-medium">/sticker</div>
            <h4 className="mt-3 text-base font-semibold text-primary-600">QR Sticker</h4>
            <p className="mt-1 text-sm text-gray-600">Durable vinyl sticker alternative</p>
          </div>
        </div>
      </div>

      {/* Interactive Calculator */}
      <div className="mx-auto mt-16 max-w-2xl">
        <div className="rounded-2xl bg-white shadow-lg ring-1 ring-black/5 p-6 sm:p-8">
          <div className="flex items-center mb-6">
            <CalculatorIcon className="h-7 w-7 text-primary-500 mr-3" />
            <h3 className="text-xl font-bold text-gray-900">Calculate Your Cost</h3>
          </div>

          {/* QR Stands Row */}
          <div className="flex items-center justify-between py-4 border-b border-gray-100">
            <div>
              <span className="text-sm font-semibold text-gray-900">QR Stands</span>
              <span className="text-xs text-gray-500 ml-2">₹{STAND_PRICE} each</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setNumStands(Math.max(0, numStands - 1))}
                aria-label="Decrease QR stand count"
                className="h-8 w-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center hover:bg-primary-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={numStands === 0}
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="w-8 text-center text-xl font-bold text-gray-900">{numStands}</span>
              <button
                onClick={() => setNumStands(Math.min(MAX_ITEMS - numStickers, numStands + 1))}
                aria-label="Increase QR stand count"
                className="h-8 w-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center hover:bg-primary-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={totalItems >= MAX_ITEMS}
              >
                <PlusIcon className="h-4 w-4" />
              </button>
              <span className="w-16 text-right text-sm font-medium text-gray-600">₹{standsCost.toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* QR Stickers Row */}
          <div className="flex items-center justify-between py-4 border-b border-gray-100">
            <div>
              <span className="text-sm font-semibold text-gray-900">QR Stickers</span>
              <span className="text-xs text-gray-500 ml-2">₹{STICKER_PRICE} each</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setNumStickers(Math.max(0, numStickers - 1))}
                aria-label="Decrease QR sticker count"
                className="h-8 w-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center hover:bg-primary-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={numStickers === 0}
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="w-8 text-center text-xl font-bold text-gray-900">{numStickers}</span>
              <button
                onClick={() => setNumStickers(Math.min(MAX_ITEMS - numStands, numStickers + 1))}
                aria-label="Increase QR sticker count"
                className="h-8 w-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center hover:bg-primary-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={totalItems >= MAX_ITEMS}
              >
                <PlusIcon className="h-4 w-4" />
              </button>
              <span className="w-16 text-right text-sm font-medium text-gray-600">₹{stickersCost.toLocaleString('en-IN')}</span>
            </div>
          </div>
          {totalItems >= MAX_ITEMS && (
            <p className="text-xs text-amber-600 mt-1 mb-2">Max 5 stands & stickers per subscription.</p>
          )}

          {/* Breakdown */}
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Digital Menu Service</span>
              <span>₹{SERVICE_PRICE.toLocaleString('en-IN')}</span>
            </div>
            {numStands > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>QR Stands ×{numStands}</span>
                <span>₹{standsCost.toLocaleString('en-IN')}</span>
              </div>
            )}
            {numStickers > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>QR Stickers ×{numStickers}</span>
                <span>₹{stickersCost.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-3 mt-3 flex justify-between items-baseline">
              <span className="text-lg font-bold text-gray-900">Total</span>
              <span className="text-lg font-bold text-gray-900" aria-live="polite">
                ₹{total.toLocaleString('en-IN')}/year
              </span>
            </div>
          </div>

          {/* Per-day callout */}
          <div className="mt-4 rounded-lg bg-highlight-100 px-4 py-3 text-center">
            <span className="text-lg font-bold text-highlight-700">
              That's just ₹{perDay}/day!
            </span>
          </div>

          {/* CTA */}
          <a
            href={getOrderWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 block rounded-md bg-accent-500 px-3.5 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-accent-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600 transition-all duration-200 hover:scale-[1.03]"
          >
            Order on WhatsApp
          </a>
        </div>
      </div>

      {/* Good to Know */}
      <div className="mt-20 mx-auto max-w-4xl">
        <div className="bg-background-100/70 rounded-xl p-8 shadow-lg border border-primary-100/50">
          <div className="flex items-center mb-5">
            <InformationCircleIcon className="h-8 w-8 text-primary-500 mr-3" />
            <h3 className="text-2xl font-bold text-text-700">Good to Know</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-text-600">
            <div className="flex items-start">
              <CheckOutlineIcon className="h-5 w-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
              <span><strong>Transparent Pricing:</strong> All prices are inclusive of applicable taxes. No surprises!</span>
            </div>
            <div className="flex items-start">
              <CheckOutlineIcon className="h-5 w-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
              <span><strong>Easy Payments:</strong> Pay easily via UPI.</span>
            </div>
            <div className="flex items-start">
              <CheckOutlineIcon className="h-5 w-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
              <span><strong>Free Delivery:</strong> QR Stands and Stickers delivered to your door at no extra charge.</span>
            </div>
            <div className="flex items-start">
              <CheckOutlineIcon className="h-5 w-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
              <span><strong>Bulk Orders?</strong> Need more than 5 tables? Contact us on WhatsApp for multi-subscription plans!</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
